// ============================================================================
//  Fase 4 — Servicio de planes de acción CX
// ----------------------------------------------------------------------------
//  Sustituye a src/lib/actions/plans.ts del prototipo (que eran server actions
//  de Next contra Supabase) por llamadas HTTP al módulo ORDS 'cx.fase4'.
//  Contrato: docs/API_FASE4.md.
//
//  Reutiliza services/api.js, así que respeta VUE_APP_ORDS_BASE igual que el
//  resto del Portal. No agrega dependencias.
//
//  CONTRATO DE ERRORES. Toda escritura responde { ERROR_ID, MENSAJE }.
//  ERROR_ID 200 = ok. 400 = regla de negocio, y el MENSAJE está redactado para
//  mostrarse al usuario tal cual. 500 = error inesperado, no se muestra crudo.
//  Este módulo convierte 400 y 500 en excepciones con un mensaje ya presentable.
// ============================================================================

import { apiGet, apiPost, apiPut } from "./api";

// ── Sesión: quién actúa ─────────────────────────────────────────────────────
//
// ⚠️ DOS LIMITACIONES CONOCIDAS, documentadas como R2 y R13 del plan:
//
// 1. Los endpoints ORDS son públicos, así que el actor viaja en el CUERPO de
//    cada petición. Las reglas se cumplen (las valida el paquete en la base),
//    pero la identidad es suplantable. Cuando ORDS exija token, este módulo
//    dejará de enviar idColaborador/rol y el backend los tomará del token.
//
// 2. El login corporativo (POST /login, id_app=13) devuelve nombre_usuario,
//    profile, rol y token — pero NO el ID_COLABORADOR. Se resuelve aquí
//    emparejando el correo contra GET /cx/catalogos. Si no hay correo en la
//    sesión o no empata con ningún colaborador activo, idColaborador queda en
//    null y el backend rechaza las escrituras que exigen actor.

const ROLES = ["cx", "admin", "responsable", "gerente", "gerente_area", "direccion"];

// El login devuelve textos variados ("CX", "Administrador", "editor"…).
// Ante la duda se asume el rol de MENOR privilegio, nunca el mayor.
export function normalizaRol(valor) {
  const s = String(valor || "").toLowerCase().trim();
  if (!s) return "responsable";
  if (ROLES.indexOf(s) !== -1) return s;
  if (s.indexOf("admin") !== -1) return "admin";
  if (s.indexOf("cx") !== -1 || s.indexOf("customer") !== -1) return "cx";
  if (s.indexOf("direc") !== -1) return "direccion";
  // "gerente de área" se revisa ANTES de "gerente" para no confundirlos.
  if (s.indexOf("area") !== -1 && s.indexOf("geren") !== -1) return "gerente_area";
  if (s.indexOf("geren") !== -1) return "gerente";
  return "responsable";
}

const leer = (k) => {
  try {
    return sessionStorage.getItem(k) || localStorage.getItem(k) || "";
  } catch (e) {
    return "";
  }
};

let _sesion = null;

// Resuelve el actor una sola vez por carga de página.
export async function sesion(forzar) {
  if (_sesion && !forzar) return _sesion;

  const correo = leer("email") || leer("usuario") || leer("nombre_usuario");
  let rol = normalizaRol(leer("rol") || leer("profile"));
  let idColaborador = null;

  const guardado = leer("idColaborador");
  if (guardado) {
    idColaborador = Number(guardado);
  }

  // Resuelve contra el catálogo: el ID_COLABORADOR (si no estaba en storage) y
  // el ROL de la base (CRM_COLABORADOR.ROL), que es la fuente de verdad del
  // scoping. El storage queda solo como respaldo cuando no hay catálogo.
  if (correo) {
    try {
      const cat = await fetchCatalogos();
      const buscado = String(correo).toLowerCase().trim();
      const c = (cat.colaboradores || []).find(
        (x) => String(x.email || "").toLowerCase().trim() === buscado
      );
      if (c) {
        if (idColaborador == null) idColaborador = c.idColaborador;
        if (c.rol) rol = normalizaRol(c.rol);
      }
    } catch (e) {
      // sin catálogo no se resuelve; idColaborador queda como estaba
    }
  }

  _sesion = { idColaborador, rol, correo, nombre: leer("nombre_usuario") };
  return _sesion;
}

// Permite fijar el colaborador a mano mientras el login no lo devuelva.
export function fijarColaborador(id) {
  try {
    sessionStorage.setItem("idColaborador", String(id));
  } catch (e) { /* almacenamiento no disponible */ }
  _sesion = null;
}

// Añade la autorización (rol + idColaborador) a una ruta de GET scoped por
// rol/acceso. Siempre incluye ambos params (fail-closed si faltan).
export function conAuth(path, s) {
  const rol = s && s.rol ? s.rol : "";
  const id = s && s.idColaborador != null ? s.idColaborador : "";
  return path + (path.indexOf("?") === -1 ? "?" : "&") +
    "rol=" + encodeURIComponent(rol) + "&idColaborador=" + encodeURIComponent(id);
}

// Adjunta el actor al cuerpo de cada escritura.
// Sin idColaborador no se envía nada: varias tablas exigen el actor (evidencias,
// comentarios) y las que no lo exigen dejarían la bitácora sin autor. Mejor
// un mensaje claro aquí que un ORA-01400 crudo desde la base.
async function conActor(body) {
  const s = await sesion();
  if (s.idColaborador == null) {
    throw new Error(
      "No se pudo identificar tu usuario como colaborador (el correo de la sesión " +
      "no coincide con ninguno activo). Pide a soporte que vincule tu cuenta o, " +
      "en pruebas, fija sessionStorage.idColaborador y recarga la página."
    );
  }
  return Object.assign({}, body, { idColaborador: s.idColaborador, rol: s.rol });
}

// ── Manejo de respuestas ────────────────────────────────────────────────────

function verifica(resp, accionDescrita) {
  const id = Number(resp && resp.ERROR_ID);
  if (id === 200) return resp;

  // 400 trae un mensaje pensado para el usuario; 500 trae el error técnico.
  // Ese técnico no se oculta: sin él, el usuario no tiene nada que reportar y
  // quien da soporte no tiene nada que investigar. Va entre paréntesis, después
  // de la frase legible, para que se entienda que no es el mensaje principal.
  let msg;
  if (id === 400 && resp.MENSAJE) {
    msg = resp.MENSAJE;
  } else {
    msg = "No se pudo " + accionDescrita + ". Intenta de nuevo o avisa a soporte.";
    if (resp && resp.MENSAJE) msg += " (" + resp.MENSAJE + ")";
  }
  const err = new Error(msg);
  err.errorId = id;
  err.detalle = resp && resp.MENSAJE;
  throw err;
}

function qs(params) {
  const partes = [];
  Object.keys(params || {}).forEach((k) => {
    const v = params[k];
    if (v !== null && v !== undefined && v !== "") {
      partes.push(encodeURIComponent(k) + "=" + encodeURIComponent(v));
    }
  });
  return partes.length ? "?" + partes.join("&") : "";
}

// ── Catálogos ───────────────────────────────────────────────────────────────

let _catalogos = null;

export async function fetchCatalogos(forzar) {
  if (_catalogos && !forzar) return _catalogos;
  const data = await apiGet("/cx/catalogos");
  _catalogos = {
    estatus: (data && data.estatus) || [],
    areas: (data && data.areas) || [],
    prioridades: (data && data.prioridades) || [],
    colaboradores: (data && data.colaboradores) || [],
  };
  return _catalogos;
}

// ── Lecturas ────────────────────────────────────────────────────────────────

// filtros: { estatus, area, responsable, claim, bandeja }
export async function fetchPlanes(filtros) {
  const data = await apiGet("/planes" + qs(filtros));
  return (data && data.items) || [];
}

export async function fetchPlan(idPlan) {
  const data = await apiGet("/planes/" + encodeURIComponent(idPlan));
  if (data && Number(data.ERROR_ID) === 404) {
    throw new Error("El plan no existe o fue eliminado.");
  }
  // Los arreglos llegan null cuando están vacíos: se normalizan para que las
  // vistas puedan iterar sin comprobar.
  return Object.assign({}, data, {
    tareas: data.tareas || [],
    evidencias: data.evidencias || [],
    comentarios: data.comentarios || [],
    historial: data.historial || [],
    validaciones: data.validaciones || [],
    cambiosFecha: data.cambiosFecha || [],
  });
}

// Bandeja "Mis pendientes": para CX son los planes por validar; para el resto,
// los suyos abiertos. Es la misma regla que aplicaba el layout del prototipo.
export async function fetchMisPendientes() {
  const s = await sesion();
  if (s.rol === "cx" || s.rol === "admin") {
    return fetchPlanes({ bandeja: "validacion" });
  }
  return fetchPlanes({ bandeja: "mis", responsable: s.idColaborador });
}

export async function fetchResumen() {
  const data = await apiGet("/cx/resumen");
  return {
    kpis: (data && data.kpis) || {},
    porEstatus: (data && data.porEstatus) || [],
    porArea: (data && data.porArea) || [],
    porCuenta: (data && data.porCuenta) || [],
  };
}

export async function fetchNotificaciones() {
  const s = await sesion();
  if (!s.idColaborador) return [];
  const data = await apiGet("/notificaciones" + qs({ idColaborador: s.idColaborador }));
  return (data && data.items) || [];
}

export async function marcarNotificacionesLeidas() {
  const s = await sesion();
  if (!s.idColaborador) return;
  const resp = await apiPut("/notificaciones/leidas", { idColaborador: s.idColaborador });
  return verifica(resp, "marcar las notificaciones como leídas");
}

// ── Escrituras ──────────────────────────────────────────────────────────────

const P = (id) => "/planes/" + encodeURIComponent(id);

export async function crearPlan(form) {
  const body = await conActor({
    idClaim: form.idClaim,
    titulo: form.titulo,
    descripcion: form.descripcion || null,
    idArea: form.idArea,
    fechaCompromiso: form.fechaCompromiso,
    resultadoEsperado: form.resultadoEsperado || null,
    criteriosAceptacion: form.criteriosAceptacion || null,
  });
  const resp = await apiPost("/planes", body);
  verifica(resp, "crear el plan de acción");
  return { idPlan: resp.idPlan, folio: resp.folio, mensaje: resp.MENSAJE };
}

// Asigna el plan al gerente de su área. El usuario no elige persona: se manda
// el gerente que GET /planes/:id ya resolvió desde
// CRM_CX_AREA.ID_COLABORADOR_GERENTE (idGerenteArea). El paquete lo deriva
// igual si llega NULL; enviarlo explícito solo lo hace compatible con
// versiones del paquete que aún exigen el responsable.
// El backend recorre pendiente_asignacion -> asignado -> pendiente_aceptacion.
export async function asignarResponsable(idPlan, idGerenteArea) {
  const body = idGerenteArea
    ? { idResponsable: idGerenteArea, idGerente: idGerenteArea }
    : {};
  const resp = await apiPut(P(idPlan) + "/asignar", await conActor(body));
  return verifica(resp, "asignar el plan al gerente del área");
}

export async function aceptarPlan(idPlan) {
  const resp = await apiPut(P(idPlan) + "/aceptar", await conActor({}));
  return verifica(resp, "aceptar la asignación");
}

export async function rechazarPlan(idPlan, motivo) {
  const resp = await apiPut(P(idPlan) + "/rechazar", await conActor({ motivo }));
  return verifica(resp, "rechazar la asignación");
}

export async function guardarCausaRaiz(idPlan, form) {
  const resp = await apiPut(P(idPlan) + "/causa-raiz", await conActor({
    causaRaiz: form.causaRaiz,
    accionContencion: form.accionContencion || null,
    solucionDefinitiva: form.solucionDefinitiva || null,
  }));
  return verifica(resp, "guardar la causa raíz");
}

export async function agregarTarea(idPlan, form) {
  const resp = await apiPost(P(idPlan) + "/tareas", await conActor({
    titulo: form.titulo,
    descripcion: form.descripcion || null,
    idResponsable: form.idResponsable || null,
    fechaCompromiso: form.fechaCompromiso,
  }));
  verifica(resp, "crear la actividad");
  return { idTarea: resp.idTarea };
}

// Devuelve el avance recalculado del plan (RN-12), para refrescar la barra sin
// tener que volver a pedir el plan completo.
export async function actualizarTarea(idPlan, idTarea, avance) {
  const resp = await apiPut(
    P(idPlan) + "/tareas/" + encodeURIComponent(idTarea),
    await conActor({ avance })
  );
  verifica(resp, "actualizar la actividad");
  return { avancePlan: resp.avancePlan };
}

export async function agregarEvidencia(idPlan, form) {
  const resp = await apiPost(P(idPlan) + "/evidencias", await conActor({
    nombre: form.nombre,
    descripcion: form.descripcion || null,
    ruta: form.ruta || null,
    urlExterna: form.urlExterna || null,
  }));
  verifica(resp, "cargar la evidencia");
  return { idEvidencia: resp.idEvidencia };
}

export async function revisarEvidencia(idPlan, idEvidencia, aprueba, comentario) {
  const resp = await apiPut(
    P(idPlan) + "/evidencias/" + encodeURIComponent(idEvidencia),
    await conActor({ aprueba: aprueba ? "S" : "N", comentario: comentario || null })
  );
  return verifica(resp, aprueba ? "aprobar la evidencia" : "rechazar la evidencia");
}

export async function enviarAValidacion(idPlan) {
  const resp = await apiPut(P(idPlan) + "/enviar-validacion", await conActor({}));
  return verifica(resp, "enviar el plan a validación");
}

export async function solicitarCorrecciones(idPlan, comentario) {
  const resp = await apiPut(P(idPlan) + "/observaciones", await conActor({ comentario }));
  return verifica(resp, "registrar las observaciones");
}

export async function cerrarPlan(idPlan, form) {
  const resp = await apiPut(P(idPlan) + "/cerrar", await conActor({
    resultadoObtenido: form.resultadoObtenido,
    leccionesAprendidas: form.leccionesAprendidas || null,
    nivelEfectividad: form.nivelEfectividad,
    calificacion: form.calificacion || null,
  }));
  return verifica(resp, "cerrar el plan");
}

export async function reabrirPlan(idPlan, motivo) {
  const resp = await apiPut(P(idPlan) + "/reabrir", await conActor({ motivo }));
  return verifica(resp, "reabrir el plan");
}

export async function solicitarCambioFecha(idPlan, form) {
  const resp = await apiPost(P(idPlan) + "/cambio-fecha", await conActor({
    fechaPropuesta: form.fechaPropuesta,
    motivo: form.motivo,
  }));
  verifica(resp, "solicitar el cambio de fecha");
  return { idCambioFecha: resp.idCambioFecha };
}

export async function resolverCambioFecha(idSolicitud, aprueba, comentario) {
  const resp = await apiPut(
    "/cambios-fecha/" + encodeURIComponent(idSolicitud),
    await conActor({ aprueba: aprueba ? "S" : "N", comentario: comentario || null })
  );
  return verifica(resp, "resolver la solicitud de cambio de fecha");
}

export async function agregarComentario(idPlan, cuerpo) {
  const resp = await apiPost(P(idPlan) + "/comentarios", await conActor({ cuerpo }));
  verifica(resp, "agregar el comentario");
  return { idComentario: resp.idComentario };
}

export const cxService = {
  sesion, fijarColaborador, normalizaRol, conAuth,
  fetchCatalogos, fetchPlanes, fetchPlan, fetchMisPendientes, fetchResumen,
  fetchNotificaciones, marcarNotificacionesLeidas,
  crearPlan, asignarResponsable, aceptarPlan, rechazarPlan, guardarCausaRaiz,
  agregarTarea, actualizarTarea,
  agregarEvidencia, revisarEvidencia,
  enviarAValidacion, solicitarCorrecciones,
  cerrarPlan, reabrirPlan,
  solicitarCambioFecha, resolverCambioFecha,
  agregarComentario,
};

export default cxService;
